import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium rounded-lg transition-colors cursor-pointer text-sm"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = getRelatedArticles(article);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '爆料网',
    },
    inLanguage: 'zh-CN',
    url: `https://baoliaowang.com/article/${article.id}`,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://baoliaowang.com' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://baoliaowang.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 爆料网`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://baoliaowang.com/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrlLarge}
      />
      <SchemaOrg schema={articleSchema} />
      <SchemaOrg schema={breadcrumbSchema} />

      <main className="pt-20">
        <article>
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: category.name, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors cursor-pointer"
                >
                  {category.name}
                </Link>
                {article.isExclusive && (
                  <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-800 rounded-full">
                    独家
                  </span>
                )}
                {article.isHot && (
                  <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                    热门
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {article.publishedAt}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {article.viewCount.toLocaleString()} 阅读
                </span>
              </div>
            </header>
          </div>

          <div className="max-w-4xl mx-auto px-4 md:px-6 mb-10">
            <div className="rounded-lg overflow-hidden bg-background-200 mb-10">
              <img
                src={article.imageUrlLarge}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={`${article.title} - 爆料网`}
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex flex-wrap items-center gap-2 mt-10 pt-8 border-t border-background-200/70">
              <span className="text-sm text-foreground-500 mr-2">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-5 bg-primary-500 rounded-full"></div>
                <h2 className="text-xl font-heading font-bold text-foreground-900">相关爆料</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {relatedArticles.map((rel) => (
                  <ArticleCard key={rel.id} article={rel} variant="compact" />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}